"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
interface FactItem {
  label: string;
  value: string;
}
interface HeroData {
  badge: string;
  heading: string;
  text: string;
  quoteBtn: string;
  secondaryBtn: string;
  imageAlt: string;
  facts: FactItem[];
}
const heroContent: Record<"en" | "zh" | "es" | "de" | "fr" | "pt", HeroData> = {
  en: {
    badge: "Industrial Filtration, Screening & Separation",
    heading: "About MutualFilter",
    text: "MutualFilter is the industrial filtration and separation brand of Hebei Hutuo Wire Mesh Products Co., Ltd., based in Anping, Hebei, China. We develop and supply stainless steel woven wire mesh, sintered wire mesh, filter components and wedge wire screens to industrial buyers, OEMs and distributors.",
    quoteBtn: "Explore Our Capabilities",
    secondaryBtn: "Browse Products",
    imageAlt: "Industrial stainless steel woven wire mesh and filtration media",
    facts: [{
      label: "Manufacturing Base",
      value: "Anping, Hebei, China"
    }, {
      label: "Standards",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  zh: {
    badge: "工业过滤、筛分与分离",
    heading: "关于 MutualFilter",
    text: "MutualFilter 是河北互拓丝网制品有限公司旗下的工业过滤与分离专业品牌，位于中国河北安平。我们为工业买家、OEM 设备制造商及分销商研发并供应不锈钢编织丝网、烧结丝网、过滤组件及楔形丝筛。",
    quoteBtn: "了解制造能力",
    secondaryBtn: "浏览产品系列",
    imageAlt: "工业不锈钢丝网及精密过滤介质",
    facts: [{
      label: "生产基地",
      value: "中国河北安平"
    }, {
      label: "执行标准",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  es: {
    badge: "Filtración, cribado y separación industrial",
    heading: "Acerca de MutualFilter",
    text: "MutualFilter es la marca de filtración y separación industrial de Hebei Hutuo Wire Mesh Products Co., Ltd., con sede en Anping, Hebei, China. Desarrollamos y suministramos malla de alambre tejida de acero inoxidable, malla sinterizada, componentes de filtración y tamices de alambre en cuña para compradores industriales, OEMs y distribuidores.",
    quoteBtn: "Explorar nuestras capacidades",
    secondaryBtn: "Ver productos",
    imageAlt: "Malla de alambre tejida de acero inoxidable y medios de filtración industrial",
    facts: [{
      label: "Base de fabricación",
      value: "Anping, Hebei, China"
    }, {
      label: "Normas",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  de: {
    badge: "Industrielle Filtration, Siebung & Trennung",
    heading: "Über MutualFilter",
    text: "MutualFilter ist die Marke für industrielle Filtration und Fest-Flüssig-Trennung von Hebei Hutuo Wire Mesh Products Co., Ltd. mit Sitz in Anping, Hebei, China. Wir entwickeln und liefern Edelstahl-Drahtgewebe, Sinterdrahtgewebe, Filterkomponenten und Spaltsiebe an Industriekunden, OEMs und Distributoren.",
    quoteBtn: "Fertigungskompetenzen entdecken",
    secondaryBtn: "Produkte durchsuchen",
    imageAlt: "Industrielles Edelstahl-Drahtgewebe und Filtrationsmedien",
    facts: [{
      label: "Produktionsstandort",
      value: "Anping, Hebei, China"
    }, {
      label: "Standards",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  fr: {
    badge: "Filtration, tamisage et séparation industrielle",
    heading: "À propos de MutualFilter",
    text: "MutualFilter est la marque de filtration et de séparation industrielle de Hebei Hutuo Wire Mesh Products Co., Ltd., basée à Anping, Hebei, Chine. Nous concevons et fournissons des toiles métalliques tissées en acier inoxydable, des toiles métalliques frittées, des composants de filtration et des crépines à fil profilé aux acheteurs industriels, équipementiers (OEM) et distributeurs.",
    quoteBtn: "Découvrir nos capacités",
    secondaryBtn: "Parcourir les produits",
    imageAlt: "Toile métallique tissée en acier inoxydable et médias de filtration industrielle",
    facts: [{
      label: "Site de fabrication",
      value: "Anping, Hebei, Chine"
    }, {
      label: "Normes",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  pt: {
    badge: "Filtração, peneiramento e separação industrial",
    heading: "Sobre a MutualFilter",
    text: "MutualFilter é a marca de filtração e separação industrial da Hebei Hutuo Wire Mesh Products Co., Ltd., sediada em Anping, Hebei, China. Desenvolvemos e fornecemos malha de arame tecida de aço inoxidável, malha sinterizada, componentes de filtração e telas de arame em cunha para compradores industriais, OEMs e distribuidores.",
    quoteBtn: "Explorar nossas capacidades",
    secondaryBtn: "Ver produtos",
    imageAlt: "Malha de arame tecida de aço inoxidável e meios de filtração industrial",
    facts: [{
      label: "Base de fabricação",
      value: "Anping, Hebei, China"
    }, {
      label: "Normas",
      value: "ISO 9044 / ASTM E2016"
    }]
  }
};
export default function Darkhero() {
  const {
    lang
  } = useLanguage();
  const content = pick(lang, heroContent);
  return <section id="herostatement" data-nav="light" className="relative w-full overflow-hidden bg-background text-foreground border-b border-border mf-hero">
      <div className="relative mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Main Statement & Text */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            {/* Engineering Eyebrow Tag */}
            <Reveal>
              <div className="inline-flex items-center gap-2 border border-border bg-muted px-3 py-1 mf-caption font-mono tracking-wider uppercase text-foreground rounded-md w-fit mb-4">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span>{content.badge}</span>
              </div>
            </Reveal>

            {/* Primary Page Heading */}
            <h1 className="mf-h1 font-default text-foreground">
              {content.heading}
            </h1>

            {/* Supporting Positioning Statement */}
            <Reveal delay={60}>
              <p className="mt-4 mf-lead text-muted-foreground leading-relaxed max-w-2xl font-normal">
                {content.text}
              </p>
            </Reveal>

            {/* Fact Strip */}
            <Reveal delay={120}>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-2xl">
                {content.facts.map((fact, i) => <div key={i} data-index={i} className="flex-1 border border-border bg-card/60 rounded-lg p-3 flex flex-col justify-between">
                    <span className="font-mono mf-caption uppercase tracking-wider text-muted-foreground block">
                      {fact.label}
                    </span>
                    <span className="text-sm font-semibold text-foreground mt-1 block">
                      {fact.value}
                    </span>
                  </div>)}
              </div>
            </Reveal>

            {/* Two Calls-to-Action */}
            <Reveal delay={180}>
              <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-semibold tracking-wide">
                  <Link to="/CapabilitiesPage" className="inline-flex items-center gap-2">
                    <span>{content.quoteBtn}</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="bg-card rounded-md font-semibold tracking-wide border-border">
                  <Link to="/ProductsPage" className="inline-flex items-center gap-2">
                    <span>{content.secondaryBtn}</span>
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Restrained Industrial Material Image */}
          <div className="lg:col-span-5 w-full">
            <Reveal delay={120}>
              <div className="relative w-full flex flex-col rounded-lg border border-border bg-card overflow-hidden gap-0 py-0">
                <div className="mf-card-media mf-ratio-hero">
                  {/* Overlaid Technical Caption Chip */}
                  <div className="absolute bottom-3 left-3 z-20 rounded bg-card px-2.5 py-1 mf-caption font-mono tracking-wider uppercase text-foreground border border-border">
                    {content.facts[0].value}
                  </div>

                  <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/stainless_steel_woven_wire_mesh_macro.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/stainless_steel_woven_wire_mesh_macro.png-wvc-sizes" alt={content.imageAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover" src="https://mutualfilter.com/wp-content/uploads/2026/10/plain-dutch-weave-mesh1.jpg" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>;
}